import cookie from 'js-cookie'

import config from '../config'
import wfeTokenRefresh, { TOKEN_KEY } from './wfeTokenRefresh'

let routes = {}

const setAuthHeader = (apiToken, wbe) => {
  if (!apiToken) {
    return {}
  }
  const key = wbe ? 'Authorization' : 'Chegg-Auth-Token'

  const val = `${wbe ? 'Bearer ' : ''}${apiToken}`

  return {
    [key]: val,
  }
}

export const setHeaders = wbe => {
  const apiToken = wbe ? cookie.get(TOKEN_KEY) : config.getToken()

  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...setAuthHeader(apiToken, wbe),
  }
}

export const request = (path, method = 'GET', data, responseType = 'json', isRetry = false) => {
  const wbe = config.getcfeAuth()

  const options = {
    method,
    headers: setHeaders(wbe),
  }

  if (data) options.body = JSON.stringify(data)

  return fetch(path, options).then(response => {
    if (response.status === 200 || response.status === 201) {
      return response[responseType]()
    }
    // token probably outdated
    // get a new token and retry the request
    if (!isRetry && [400, 401, 403].includes(response.status)) {
      if (wbe) {
        return wfeTokenRefresh(response).then(() => request(path, method, data, responseType, true))
      }
      return config.refreshToken().then(() => request(path, method, data, responseType, true))
    }

    // eslint-disable-next-line
    return Promise.reject({ status: response.status })
  })
}

// like fetch() but with support for http://user:passwort@host
const fetchWithBasicAuth = (url, options = {}) => {
  /* eslint-disable no-param-reassign, no-useless-escape */
  const match = url.match(/\/(([^\/:]+):([^@]+)@)/)
  if (match) {
    const token = btoa(`${match[2]}:${match[3]}`)
    if (!options.headers) {
      options.headers = {}
    }
    options.headers.Authorization = `Basic ${token}`
    url = url.replace(match[1], '')
  }
  /* eslint-enable no-param-reassign */
  return fetch(url, options)
}

const fromHal = (key, root, base = '_links') => (root[base] instanceof Object ? root[base][key] : undefined)
const halRequest = (key, root, data, params) => {
  const link = fromHal(key, root)
  if (!link || !link.href) {
    throw new Error(`could not find link ${key} in _links of ${JSON.stringify(root)}`)
  }

  let path = link.href
  if (params) {
    Object.keys(params).forEach(k => {
      path = path.replace(`{${k}}`, params[k])
    })
  }

  return request(path, link.method, data)
}

const Api = {
  setApiRoot(url) {
    this.rootUrl = url
  },

  // getApiRoot purposefully differs from WPT - should not deduce but use exactly
  // what is passed in.
  //   Unlike https://github.com/easybib/wpt/blob/master/frontend/src/api.js#L75-L97
  getApiRoot() {
    return this.rootUrl
  },
  init(routeConfig) {
    routes = routeConfig
  },
  route(name, params = {}) {
    if (!routes || !routes[name]) throw new Error(`route ${name} not found`)

    let path = routes[name].href
    Object.keys(params).forEach(key => {
      path = path.replace(`{${key}}`, params[key])
    })
    return path
  },
  createDocument(data) {
    const endpoint = `${data.createUrl}?isDraftjs=true`
    return request(endpoint, 'POST', data)
  },
  loadDocument(uploadId) {
    return request(this.route('uploadDetail', { uploadId }))
  },
  getUploadPolicy(options) {
    return request(this.route('uploadPolicy'), 'POST', options).then(data =>
      // See explanation -> https://github.com/easybib/issues/issues/6920#issuecomment-357538146
      // In upload widget, getUploadPolicy is the action that
      //  creates a user + session. Return these vals here to initializeAuthentication
      ({
        createUrl: fromHal('uploadCreate', data).href,
        signedUrl: fromHal('signedUrl', data).href,
        authentication: { user: data.user, session: data.session, citationToken: data.citationToken },
      })
    )
  },
  loadConfig() {
    const apiUrl = this.getApiRoot()
    return request(`${apiUrl}/config`).then(data => {
      if (!data.user) {
        // eslint-disable-next-line no-param-reassign
        data.user = {
          metadata: {},
        }
      }
      return data
    })
  },
  pollWorkerStatus(data) {
    return halRequest('workerStatus', data)
  },
  loginPlusUser(tokens) {
    const apiUrl = this.getApiRoot()
    return request(`${apiUrl}/auth/plus`, 'POST', tokens)
  },
  logoutOnHostSite() {
    if (config.bibme && config.bibme.logoutUrl) {
      const options = { method: 'DELETE', credentials: 'include' }
      fetchWithBasicAuth(config.bibme.logoutUrl, options)
    }

    if (config.easybib && config.easybib.logoutUrl) {
      fetch(config.easybib.logoutUrl)
    }
  },
}

export default Api
